import { z, type ZodType } from '@customerio/hydra-validation';
import type { IntegrationsPipelinesConnection as UIAPIPipelinesConnection } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Pipelines connection entity — a workspace's connected CDP sources /
 * destinations (`GET /environments/:id/integrations/pipelines`). This is the
 * only fully-typed integrations read; the "spreadsheet" `GET /integrations`
 * catalog list has no typed contract yet (needs a Services OpenAPI change).
 *
 * `created_at` / `updated_at` / `last_activity` are unix **seconds** on the
 * wire. We keep them as raw numbers (like `domain`) rather than transforming
 * to `Date`: the only current consumer (the command-dialog integrations
 * source) already does its own sec→ms conversion. Revisit if a date-aware
 * consumer lands.
 */
export type PipelinesConnection = UIAPIPipelinesConnection;

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const PipelinesConnectionFromApi = z.looseObject({
  id: z.string(),
  status: z.string(),
}) as unknown as ZodType<PipelinesConnection, unknown>;

/** @public */
export const pipelinesConnection = defineEntity<PipelinesConnection, PipelinesConnection, void, undefined, 'workspace'>(
  {
    name: 'pipelines_connection',
    scope: 'workspace',
    pk: (c) => c.id,
    schema: { read: PipelinesConnectionFromApi },
  },
);

export const pipelinesConnectionKeys = pipelinesConnection.keys;

export function parsePipelinesConnection(raw: UIAPIPipelinesConnection): PipelinesConnection {
  return pipelinesConnection.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Integration catalog ("spreadsheet") list                                  */
/* ------------------------------------------------------------------------- */

/** A configurable field on a connector detail page (only detail pages read it). */
export interface IntegrationSetting {
  allowed_values: unknown;
  default: false | null;
  description: string;
  key: string;
  label: string;
  placeholder: string;
  secret: boolean;
  type: string;
}

/**
 * A backend catalog integration from `GET /v1/environments/:id/integrations`.
 * This is the parse target for `parseIntegration`, which takes `unknown`, so it
 * is not checked against the generated schema — keep it aligned with
 * `services_ui_api_integrations_Integration` by hand. Fields stay snake_case
 * (the wire shape) — the view-layer derived helpers read them directly.
 *
 * `details_page_title` is optional: the handler never returns it (detail pages
 * source the title elsewhere). `use_cases` and `settings` are optional because
 * the handler omits them: pipelines catalog entries set neither, and the list
 * handlers never populate `settings` at all (services#24411).
 */
export interface Integration {
  id: string;
  category: string;
  name: string;
  beta: boolean;
  image_url?: string;
  overview: string;
  description: string;
  details_page_description: string;
  details_page_title?: string;
  subtitle?: string;
  use_cases?: string[];
  cta_label: string;
  cta_url: string;
  type: string;
  homepage?: string;
  connection_type?: string;
  settings?: IntegrationSetting[];
  direction?: string;
  is_premium: boolean;
}

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const IntegrationFromApi = z.looseObject({
  id: z.string(),
}) as unknown as ZodType<Integration, unknown>;

/** @public */
export const integration = defineEntity<Integration, Integration, void, undefined, 'workspace'>({
  name: 'integration',
  scope: 'workspace',
  pk: (i) => i.id,
  schema: { read: IntegrationFromApi },
});

export const integrationKeys = integration.keys;

export function parseIntegration(raw: unknown): Integration {
  return integration.parse(raw);
}
